import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
  InputAdornment,
} from '@mui/material';
import { PostUserInfo } from '../../../services/apiTypes';
import { useTranslation } from 'react-i18next';
import { LANGUAGE, ROLE, DATE_FORMAT } from '../../../_constants/Const';
import UserOrganizationTree from './UserOrganizationTree';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { MaxLength } from '../../../_constants/maxLengthConst';
import { PARTS_LIST_DISPLAY_PATTERN } from '../../../_constants/manageConst';
import { isStringInput } from '../../../_utils/utils';

type UserRegisterModalProps = {
  errorMsg: string;
  updateErrorMsg: (data: string) => void;
  isOpen: boolean;
  handleUserRegist: (postTargetUser: PostUserInfo) => Promise<void>;
  handleClose: () => void;
};

const styleButton = {
  width: '120px',
  mx: 2,
  my: 1,
};

const defaultUserRegisterData: PostUserInfo = {
  userId: '',
  name: '',
  roleId: 1,
  companyKey: '',
  language: 'en',
  password: '',
  dateFormat: 0,
  partsListDisplayPattern: 1,
};

const UserRegisterModal: React.FC<UserRegisterModalProps> = ({
  errorMsg,
  updateErrorMsg,
  isOpen,
  handleUserRegist,
  handleClose,
}) => {
  const { t } = useTranslation();

  const { accountsInfoData } = useSelector(
    (state: RootState) => state.accountsInfo,
  );

  /** ユーザーの登録情報を取る */
  const [targetUser, setTargetUser] = useState<PostUserInfo>(
    defaultUserRegisterData,
  );

  /** パスワードが一致していることを確認 */
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const isPasswordMismatch = confirmPassword !== targetUser.password;

  /** 全てフィールドを確認 */
  const [isDisabledAddBtn, setIsDisabledAddBtn] = useState(true);

  /** 処理中フラグ */
  const isProcessing = useRef(false);

  /** 組織ツリー*/
  const setSelectedCompany = (company: string) => {
    setTargetUser((prev) => ({
      ...prev,
      companyKey: company,
    }));
  };
  const selectedCompany = useMemo(
    () =>
      accountsInfoData.companyList.find(
        (item) => item.key === targetUser.companyKey,
      )?.name ?? targetUser.companyKey,
    [accountsInfoData, targetUser.companyKey],
  );

  useEffect(() => {
    if (!isOpen) {
      updateErrorMsg('');
      setTargetUser(defaultUserRegisterData);
      setConfirmPassword('');
    }
  }, [isOpen]);

  useEffect(() => {
    const isAllFilled =
      Object.values(targetUser).every(
        (val) =>
          typeof val === 'number' ||
          typeof val === 'boolean' ||
          isStringInput(val),
      ) && confirmPassword.trim() !== '';

    setIsDisabledAddBtn(!isAllFilled || isPasswordMismatch);
  }, [targetUser, confirmPassword, isPasswordMismatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateErrorMsg('');
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
      setConfirmPasswordTouched(true);
    } else {
      setTargetUser((prev) => ({
        ...prev,
        [name]:
          typeof prev[name as keyof PostUserInfo] === 'number'
            ? Number(value)
            : value,
      }));
    }
  };

  /** モーダルを閉じる */
  const closeModal = (isClose: boolean) => {
    if (isClose === true) {
      handleClose();
    }
  };

  /** OKボタン押下 */
  const onClickOk = async () => {
    if (isProcessing.current) {
      // 連打防止
      return;
    }
    isProcessing.current = true;
    try {
      updateErrorMsg('');
      await handleUserRegist(targetUser);
    } finally {
      isProcessing.current = false;
    }
  };

  return (
    <Dialog open={isOpen} onClose={closeModal} closeAfterTransition={false}>
      <DialogTitle>{t('LBL_REGISTER')}</DialogTitle>
      <DialogContent>
        {errorMsg && (
          <Typography color="error" sx={{ mb: 1 }}>
            {errorMsg}
          </Typography>
        )}
        <TextField
          autoFocus
          margin="dense"
          label={t('LBL_USER_ID')}
          name="userId"
          fullWidth
          value={targetUser.userId}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: { maxLength: MaxLength.ManageUser.USER_ID },
          }}
          sx={{ bgcolor: 'warning.light' }}
        />
        <TextField
          margin="dense"
          label={t('LBL_NAME')}
          name="name"
          fullWidth
          value={targetUser.name}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: { maxLength: MaxLength.ManageUser.NAME },
          }}
          sx={{ bgcolor: 'warning.light' }}
        />
        <TextField
          margin="dense"
          label={t('LBL_ROLE')}
          name="roleId"
          select
          fullWidth
          value={targetUser.roleId}
          onChange={handleInputChange}
        >
          {Object.keys(ROLE).map((key) => (
            <MenuItem key={key} value={Number(key)}>
              {t(ROLE[Number(key)])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_COMPANY')}
          name="companyKey"
          fullWidth
          value={selectedCompany}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <UserOrganizationTree
                    setSelectedCompany={setSelectedCompany}
                    selectedCompanyKey={targetUser.companyKey}
                  />
                </InputAdornment>
              ),
            },
            inputLabel: {
              shrink: !!selectedCompany,
            },
          }}
          sx={{ bgcolor: 'warning.light' }}
        />
        <TextField
          margin="dense"
          label={t('LBL_DEFAULT_LANGUAGE')}
          name="language"
          select
          fullWidth
          value={targetUser.language}
          onChange={handleInputChange}
        >
          {Object.keys(LANGUAGE).map((key) => (
            <MenuItem key={key} value={key}>
              {t(LANGUAGE[key])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_DATE_FORMAT')}
          name="dateFormat"
          select
          fullWidth
          value={targetUser.dateFormat}
          onChange={handleInputChange}
        >
          {Object.keys(DATE_FORMAT).map((key) => (
            <MenuItem key={key} value={Number(key)}>
              {t(DATE_FORMAT[Number(key)])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_PARTS_DISPLAY_PATTERN')}
          name="partsListDisplayPattern"
          select
          fullWidth
          value={targetUser.partsListDisplayPattern}
          onChange={handleInputChange}
        >
          {Object.values(PARTS_LIST_DISPLAY_PATTERN).map((value) => (
            <MenuItem key={value.id} value={value.id}>
              {t(value.key)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_PASSWORD')}
          name="password"
          type="password"
          autoComplete="new-password"
          fullWidth
          value={targetUser.password}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: { maxLength: MaxLength.ManageUser.PASSWORD },
          }}
          sx={{ bgcolor: 'warning.light' }}
        />
        <TextField
          margin="dense"
          label={t('LBL_CONFIRM_PASSWORD')}
          name="confirmPassword"
          type="password"
          autoComplete="off"
          fullWidth
          value={confirmPassword}
          onChange={handleInputChange}
          error={isPasswordMismatch && confirmPasswordTouched}
          helperText={
            isPasswordMismatch && confirmPasswordTouched
              ? t('ERR_CONFIRM_PASSWORD')
              : ''
          }
          slotProps={{
            htmlInput: { maxLength: MaxLength.ManageUser.CONFIRM_PASSWORD },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          sx={styleButton}
          variant="contained"
          onClick={() => closeModal(true)}
        >
          {t('BTN_CANCEL')}
        </Button>
        <Button
          color="primary"
          sx={styleButton}
          variant="contained"
          onClick={() => onClickOk()}
          disabled={isDisabledAddBtn || isProcessing.current}
        >
          {t('BTN_ADD')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserRegisterModal;
